/**
 * Problem Report Types
 * TypeScript interfaces and enums for problem report feature
 */

// Enums matching backend
export enum ProblemSeverity {
  Low = 0,
  Medium = 1,
  High = 2,
  Critical = 3
}

export enum ProblemCategory {
  Bug = 0,
  UIIssue = 1,
  Performance = 2,
  DataIssue = 3,
  FeatureRequest = 4,
  Other = 5
}

export enum ProblemReportStatus {
  New = 0,
  UnderReview = 1,
  InProgress = 2,
  Resolved = 3,
  WontFix = 4,
  Duplicate = 5
}

// Display label helpers
export const SeverityLabels: Record<ProblemSeverity, string> = {
  [ProblemSeverity.Low]: 'Low',
  [ProblemSeverity.Medium]: 'Medium',
  [ProblemSeverity.High]: 'High',
  [ProblemSeverity.Critical]: 'Critical'
};

export const CategoryLabels: Record<ProblemCategory, string> = {
  [ProblemCategory.Bug]: 'Bug',
  [ProblemCategory.UIIssue]: 'UI Issue',
  [ProblemCategory.Performance]: 'Performance',
  [ProblemCategory.DataIssue]: 'Data Issue',
  [ProblemCategory.FeatureRequest]: 'Feature Request',
  [ProblemCategory.Other]: 'Other'
};

export const StatusLabels: Record<ProblemReportStatus, string> = {
  [ProblemReportStatus.New]: 'New',
  [ProblemReportStatus.UnderReview]: 'Under Review',
  [ProblemReportStatus.InProgress]: 'In Progress',
  [ProblemReportStatus.Resolved]: 'Resolved',
  [ProblemReportStatus.WontFix]: "Won't Fix",
  [ProblemReportStatus.Duplicate]: 'Duplicate'
};

// Severity colors for badges
export const SeverityColors: Record<ProblemSeverity, string> = {
  [ProblemSeverity.Low]: 'bg-green-500/20 text-green-400 border-green-500/30',
  [ProblemSeverity.Medium]: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  [ProblemSeverity.High]: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  [ProblemSeverity.Critical]: 'bg-red-500/20 text-red-400 border-red-500/30'
};

// Status colors for badges
export const StatusColors: Record<ProblemReportStatus, string> = {
  [ProblemReportStatus.New]: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  [ProblemReportStatus.UnderReview]: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  [ProblemReportStatus.InProgress]: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  [ProblemReportStatus.Resolved]: 'bg-green-500/20 text-green-400 border-green-500/30',
  [ProblemReportStatus.WontFix]: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  [ProblemReportStatus.Duplicate]: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
};

// Request DTOs
export interface CreateProblemReportRequest {
  description: string;
  severity: ProblemSeverity;
  category: ProblemCategory;
  pageUrl?: string;
  userAgent?: string;
  screenshotData?: string;
  screenshotContentType?: string;
}

// Response DTOs
export interface ProblemReportResponse {
  id: string;
  description: string;
  severity: ProblemSeverity;
  severityDisplay: string;
  category: ProblemCategory;
  categoryDisplay: string;
  status: ProblemReportStatus;
  statusDisplay: string;
  pageUrl?: string;
  hasScreenshot: boolean;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

export interface AdminProblemReportResponse {
  id: string;
  description: string;
  severity: ProblemSeverity;
  severityDisplay: string;
  category: ProblemCategory;
  categoryDisplay: string;
  status: ProblemReportStatus;
  statusDisplay: string;
  pageUrl?: string;
  userAgent?: string;
  hasScreenshot: boolean;
  screenshotData?: string;
  screenshotContentType?: string;
  adminNotes?: string;
  userId: string;
  userName: string;
  userEmail: string;
  clientId?: string;
  clientName?: string;
  assignedToUserId?: string;
  assignedToName?: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

export interface ProblemReportsListResponse {
  reports: AdminProblemReportResponse[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface CreateProblemReportResponse {
  id: string;
  message: string;
  success: boolean;
}
